import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  NumberInput,
  NumberInputField,
  Grid,
  Image,
  IconButton,
  useToast,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  Heading,
  Divider,
  Code,
  Link,
  Checkbox,
  Switch,
  FormHelperText,
  Select,
  Spinner
} from '@chakra-ui/react';
import { DeleteIcon, ExternalLinkIcon, CopyIcon, AddIcon } from '@chakra-ui/icons';
import { createGallery, getGalleryById, addImagesToGallery, removeImageFromGallery } from '../lib/galleryService';
import { sendGalleryNotification } from '../lib/resendEmailService';
import { Link as RouterLink, useParams } from 'react-router-dom';
import AdminAuth from '../components/AdminAuth';

interface UploadedImage {
  file: File;
  preview: string;
  id: string;
}

interface ExistingImage {
  id: string;
  filename: string;
  preview_url: string;
  original_url: string;
  position: number;
}

const AdminGallery: React.FC = () => {
  const { galleryId } = useParams<{ galleryId?: string }>();
  const isEditMode = Boolean(galleryId);
  
  const [clientName, setClientName] = useState('');
  const [galleryTitle, setGalleryTitle] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [galleryPrice, setGalleryPrice] = useState(25);
  const [galleryLanguage, setGalleryLanguage] = useState('en');
  const [hasExpiration, setHasExpiration] = useState(false);
  const [expirationDays, setExpirationDays] = useState(30);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [selectedExistingImages, setSelectedExistingImages] = useState<Set<string>>(new Set());
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [existingImages, setExistingImages] = useState<ExistingImage[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingImages, setIsAddingImages] = useState(false);
  const [isRemovingImages, setIsRemovingImages] = useState(false);
  const [createdGallery, setCreatedGallery] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const generateToken = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  // Load gallery data when in edit mode
  useEffect(() => {
    if (isEditMode && galleryId) {
      loadGalleryData();
    }
  }, [isEditMode, galleryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGalleryData = async () => {
    if (!galleryId) return;
    
    setIsLoading(true);
    try {
      const result = await getGalleryById(galleryId);
      if (result.success && result.gallery) {
        const gallery = result.gallery;
        setClientName(gallery.client_name);
        setGalleryTitle(gallery.title);
        setClientEmail(gallery.client_email || '');
        setGalleryPrice(gallery.price_per_image);
        setHasExpiration(Boolean(gallery.expires_at));
        
        if (gallery.expires_at) {
          const expiryDate = new Date(gallery.expires_at);
          const now = new Date();
          const diffDays = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          setExpirationDays(Math.max(1, diffDays));
        }
        
        setExistingImages(gallery.images || []);
      } else {
        toast({
          title: 'Error loading gallery',
          description: result.error || 'Gallery not found',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error loading gallery',
        description: 'Failed to load gallery data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const id = generateToken();
        const preview = URL.createObjectURL(file);
        
        setUploadedImages(prev => [...prev, {
          file,
          preview,
          id
        }]);
      }
    });
  };

  const removeImage = (id: string) => {
    setUploadedImages(prev => {
      const image = prev.find(img => img.id === id);
      if (image) {
        URL.revokeObjectURL(image.preview);
      }
      return prev.filter(img => img.id !== id);
    });
    // Remove from selection if it was selected
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const toggleImageSelection = (id: string) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAllImages = () => {
    setSelectedImages(new Set(uploadedImages.map(img => img.id)));
  };

  const deselectAllImages = () => {
    setSelectedImages(new Set());
  };

  const removeSelectedImages = () => {
    uploadedImages.forEach(img => {
      if (selectedImages.has(img.id)) {
        URL.revokeObjectURL(img.preview);
      }
    });
    setUploadedImages(prev => prev.filter(img => !selectedImages.has(img.id)));
    setSelectedImages(new Set());
  };

  // Existing image selection functions
  const toggleExistingImageSelection = (id: string) => {
    setSelectedExistingImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAllExistingImages = () => {
    setSelectedExistingImages(new Set(existingImages.map(img => img.id)));
  };

  const deselectAllExistingImages = () => {
    setSelectedExistingImages(new Set());
  };

  const removeSelectedExistingImages = async () => {
    if (selectedExistingImages.size === 0) return;

    setIsRemovingImages(true);
    try {
      const promises = Array.from(selectedExistingImages).map(imageId => 
        removeImageFromGallery(imageId)
      );
      
      const results = await Promise.all(promises);
      const successful = results.filter(r => r).length;
      
      toast({
        title: `Removed ${successful} images`,
        description: `Successfully removed ${successful} out of ${selectedExistingImages.size} selected images`,
        status: successful === selectedExistingImages.size ? 'success' : 'warning',
        duration: 3000,
        isClosable: true,
      });

      // Refresh gallery data
      await loadGalleryData();
      setSelectedExistingImages(new Set());
    } catch (error) {
      toast({
        title: 'Error removing images',
        description: 'Failed to remove some images',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRemovingImages(false);
    }
  };

  const addNewImages = async () => {
    if (!galleryId || uploadedImages.length === 0) return;

    setIsAddingImages(true);
    try {
      const imageFiles = uploadedImages.map(img => img.file);
      const result = await addImagesToGallery(galleryId, imageFiles);

      if (result.success) {
        toast({
          title: 'Images added successfully!',
          description: `Added ${uploadedImages.length} new images to the gallery`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Clear uploaded images and refresh gallery data
        uploadedImages.forEach(img => URL.revokeObjectURL(img.preview));
        setUploadedImages([]);
        setSelectedImages(new Set());
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        await loadGalleryData();
      } else {
        throw new Error(result.error || 'Failed to add images');
      }
    } catch (error) {
      toast({
        title: 'Error adding images',
        description: error instanceof Error ? error.message : 'Failed to add images',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAddingImages(false);
    }
  };

  const createGalleryHandler = async () => {
    if (!clientName || !galleryTitle || uploadedImages.length === 0) {
      toast({
        title: 'Missing information',
        description: 'Please fill all fields and upload at least one image',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCreating(true);

    try {
      // Extract files from uploaded images
      const imageFiles = uploadedImages.map(img => img.file);

      // Use Supabase service
      const result = await createGallery(
        galleryTitle, 
        clientName, 
        galleryPrice, 
        imageFiles,
        hasExpiration ? expirationDays : undefined,
        clientEmail || undefined
      );

      if (result.success && result.galleryToken && result.galleryId) {
        setCreatedGallery(result.galleryToken);


        // Send email notification if client email is provided
        if (clientEmail && clientEmail.trim()) {
          try {
            console.log('Sending gallery notification email to:', clientEmail);
            const emailResult = await sendGalleryNotification({
              clientEmail: clientEmail.trim(),
              clientName,
              galleryTitle,
              galleryToken: result.galleryToken,
              expiresAt: hasExpiration ? new Date(Date.now() + expirationDays * 24 * 60 * 60 * 1000).toISOString() : undefined,
              imageCount: uploadedImages.length,
              price: galleryPrice,
              language: galleryLanguage
            });

            if (emailResult.success) {
              toast({
                title: 'Gallery created and email sent!',
                description: `Gallery created successfully and notification sent to ${clientEmail}`,
                status: 'success',
                duration: 7000,
                isClosable: true,
              });
            } else {
              console.warn('Email sending failed:', emailResult.error);
              toast({
                title: 'Gallery created (email failed)',
                description: `Gallery created but email notification failed: ${emailResult.error}`,
                status: 'warning',
                duration: 7000,
                isClosable: true,
              });
            }
          } catch (emailError) {
            console.error('Email sending error:', emailError);
            toast({
              title: 'Gallery created (email failed)',
              description: 'Gallery created successfully but email notification failed',
              status: 'warning',
              duration: 7000,
              isClosable: true,
            });
          }
        } else {
          toast({
            title: 'Gallery created successfully!',
            description: `Gallery token: ${result.galleryToken}`,
            status: 'success',
            duration: 7000,
            isClosable: true,
          });
        }
      } else {
        throw new Error(result.error || 'Failed to create gallery');
      }

    } catch (error) {
      console.error('Error creating gallery:', error);
      toast({
        title: 'Error creating gallery',
        description: error instanceof Error ? error.message : 'Please check your Supabase configuration',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      status: 'success',
      duration: 2000,
    });
  };

  const resetForm = () => {
    setClientName('');
    setGalleryTitle('');
    setClientEmail('');
    setGalleryPrice(25);
    setGalleryLanguage('en');
    setHasExpiration(false);
    setExpirationDays(30);
    setSelectedImages(new Set());
    uploadedImages.forEach(img => URL.revokeObjectURL(img.preview));
    setUploadedImages([]);
    setCreatedGallery(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <AdminAuth title="Gallery Creation" description="Enter the admin password to create galleries">
      <Box maxW="container.lg" mx="auto" p={8}>
        <VStack spacing={8} align="stretch">
          <VStack align="start" spacing={1}>
            <Heading size="lg">
              {isEditMode ? 'Edit Gallery' : 'Create Client Gallery'}
            </Heading>
            <Text fontSize="sm" color="gray.600">
              <RouterLink to={isEditMode ? "/admin/galleries" : "/admin"} style={{ color: 'inherit' }}>
                ← Back to {isEditMode ? 'Galleries' : 'Dashboard'}
              </RouterLink>
            </Text>
          </VStack>

          {isLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
              <Spinner size="xl" />
            </Box>
          )}

        {!createdGallery && !isLoading ? (
          <>
            {/* Existing Images Section (Edit Mode Only) */}
            {isEditMode && existingImages.length > 0 && (
              <Card>
                <CardBody>
                  <VStack spacing={4}>
                    <Text fontSize="lg" fontWeight="bold">Current Gallery Images ({existingImages.length})</Text>
                    
                    <HStack justify="space-between" w="100%">
                      <Text color="gray.600">{existingImages.length} images in gallery</Text>
                      <HStack spacing={2}>
                        <Button size="sm" variant="outline" onClick={selectAllExistingImages}>
                          Select All
                        </Button>
                        <Button size="sm" variant="outline" onClick={deselectAllExistingImages}>
                          Deselect All
                        </Button>
                        {selectedExistingImages.size > 0 && (
                          <Button 
                            size="sm" 
                            colorScheme="red" 
                            variant="outline"
                            onClick={removeSelectedExistingImages}
                            isLoading={isRemovingImages}
                            loadingText={`Removing ${selectedExistingImages.size}`}
                          >
                            Remove Selected ({selectedExistingImages.size})
                          </Button>
                        )}
                      </HStack>
                    </HStack>
                    
                    <Grid templateColumns="repeat(auto-fill, minmax(150px, 1fr))" gap={4} w="100%">
                      {existingImages.map(image => (
                        <Box 
                          key={image.id} 
                          position="relative"
                          border={selectedExistingImages.has(image.id) ? "3px solid" : "2px solid"}
                          borderColor={selectedExistingImages.has(image.id) ? "blue.500" : "transparent"}
                          borderRadius="md"
                          cursor="pointer"
                          onClick={() => toggleExistingImageSelection(image.id)}
                        >
                          <Image
                            src={image.preview_url}
                            alt=""
                            w="100%"
                            h="150px"
                            objectFit="cover"
                            borderRadius="md"
                          />
                          
                          {/* Selection checkbox */}
                          <Checkbox
                            isChecked={selectedExistingImages.has(image.id)}
                            position="absolute"
                            top={2}
                            left={2}
                            bg="white"
                            borderRadius="sm"
                            onChange={() => toggleExistingImageSelection(image.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          
                          {/* Individual delete button */}
                          <IconButton
                            aria-label="Remove image"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            position="absolute"
                            top={2}
                            right={2}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const success = await removeImageFromGallery(image.id);
                              if (success) {
                                toast({
                                  title: 'Image removed',
                                  status: 'success',
                                  duration: 2000,
                                });
                                await loadGalleryData();
                              } else {
                                toast({
                                  title: 'Failed to remove image',
                                  status: 'error',
                                  duration: 3000,
                                });
                              }
                            }}
                          />
                        </Box>
                      ))}
                    </Grid>
                  </VStack>
                </CardBody>
              </Card>
            )}

            <Card>
              <CardBody>
                <VStack spacing={6}>
                  <HStack spacing={6} w="100%">
                    <FormControl>
                      <FormLabel>Client Name</FormLabel>
                      <Input
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="María García"
                        isDisabled={isEditMode}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Gallery Title</FormLabel>
                      <Input
                        value={galleryTitle}
                        onChange={(e) => setGalleryTitle(e.target.value)}
                        placeholder="Beach Photoshoot - October 2024"
                        isDisabled={isEditMode}
                      />
                    </FormControl>
                  </HStack>

                  <HStack spacing={6} w="100%">
                    <FormControl>
                      <FormLabel>Client Email (optional)</FormLabel>
                      <Input
                        type="email"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        placeholder="maria@example.com"
                        isDisabled={isEditMode}
                      />
                      <FormHelperText>
                        Client email for record keeping (optional).
                      </FormHelperText>
                    </FormControl>

                    <FormControl w="200px">
                      <FormLabel>Gallery Language</FormLabel>
                      <Select
                        value={galleryLanguage}
                        onChange={(e) => setGalleryLanguage(e.target.value)}
                        isDisabled={isEditMode}
                      >
                        <option value="en">English</option>
                        <option value="es">Español</option>
                      </Select>
                      <FormHelperText>
                        Language for client emails and gallery interface
                      </FormHelperText>
                    </FormControl>
                  </HStack>

                  <HStack spacing={6} w="100%">
                    <FormControl w="200px">
                      <FormLabel>Total Gallery Price (€)</FormLabel>
                      <Input
                        type="number"
                        value={galleryPrice}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          setGalleryPrice(isNaN(value) ? 0 : value);
                        }}
                        min={0}
                        step={0.01}
                        placeholder="25.00"
                        isDisabled={isEditMode}
                      />
                      <FormHelperText>Price for complete gallery access. Supports decimals (e.g., 0.2, 1.5, 12.50)</FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Gallery Expiration</FormLabel>
                      <VStack align="start" spacing={3}>
                        <Switch
                          isChecked={hasExpiration}
                          onChange={(e) => setHasExpiration(e.target.checked)}
                          isDisabled={isEditMode}
                        />
                        <Text fontSize="sm" color="gray.600">
                          {hasExpiration ? 'Gallery will expire' : 'Gallery never expires'}
                        </Text>
                        {hasExpiration && (
                          <NumberInput
                            value={expirationDays}
                            onChange={(_, num) => setExpirationDays(num || 30)}
                            min={1}
                            max={365}
                            w="120px"
                            isDisabled={isEditMode}
                          >
                            <NumberInputField />
                          </NumberInput>
                        )}
                        {hasExpiration && (
                          <Text fontSize="xs" color="gray.500">
                            Expires in {expirationDays} days
                          </Text>
                        )}
                      </VStack>
                    </FormControl>
                  </HStack>

                </VStack>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <VStack spacing={4}>
                  <Text fontSize="lg" fontWeight="bold">
                    {isEditMode ? 'Add New Images' : 'Upload Images'}
                  </Text>
                  
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    colorScheme="blue"
                    size="lg"
                  >
                    Select Images
                  </Button>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />

                  {uploadedImages.length > 0 && (
                    <>
                      <HStack justify="space-between" w="100%">
                        <Text color="gray.600">{uploadedImages.length} images uploaded</Text>
                        <HStack spacing={2}>
                          <Button size="sm" variant="outline" onClick={selectAllImages}>
                            Select All
                          </Button>
                          <Button size="sm" variant="outline" onClick={deselectAllImages}>
                            Deselect All
                          </Button>
                          {selectedImages.size > 0 && (
                            <Button 
                              size="sm" 
                              colorScheme="red" 
                              variant="outline"
                              onClick={removeSelectedImages}
                            >
                              Remove Selected ({selectedImages.size})
                            </Button>
                          )}
                        </HStack>
                      </HStack>
                      
                      <Grid templateColumns="repeat(auto-fill, minmax(150px, 1fr))" gap={4} w="100%">
                        {uploadedImages.map(image => (
                          <Box 
                            key={image.id} 
                            position="relative"
                            border={selectedImages.has(image.id) ? "3px solid" : "2px solid"}
                            borderColor={selectedImages.has(image.id) ? "blue.500" : "transparent"}
                            borderRadius="md"
                            cursor="pointer"
                            onClick={() => toggleImageSelection(image.id)}
                          >
                            <Image
                              src={image.preview}
                              alt=""
                              w="100%"
                              h="150px"
                              objectFit="cover"
                              borderRadius="md"
                            />
                            
                            {/* Selection checkbox */}
                            <Checkbox
                              isChecked={selectedImages.has(image.id)}
                              position="absolute"
                              top={2}
                              left={2}
                              bg="white"
                              borderRadius="sm"
                              onChange={() => toggleImageSelection(image.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            
                            {/* Individual delete button */}
                            <IconButton
                              aria-label="Remove image"
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              position="absolute"
                              top={2}
                              right={2}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeImage(image.id);
                              }}
                            />
                          </Box>
                        ))}
                      </Grid>
                    </>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {isEditMode ? (
              uploadedImages.length > 0 && (
                <Button
                  onClick={addNewImages}
                  colorScheme="green"
                  size="lg"
                  isLoading={isAddingImages}
                  loadingText="Adding Images..."
                  leftIcon={<AddIcon />}
                >
                  Add {uploadedImages.length} Images to Gallery
                </Button>
              )
            ) : (
              <Button
                onClick={createGalleryHandler}
                colorScheme="green"
                size="lg"
                isLoading={isCreating}
                loadingText="Creating Gallery..."
              >
                Create Gallery
              </Button>
            )}
          </>
        ) : (
          <Card>
            <CardBody>
              <VStack spacing={6} align="center">
                <Alert status="success">
                  <AlertIcon />
                  Gallery created successfully!
                </Alert>

                <VStack spacing={4}>
                  <Text fontSize="lg" fontWeight="bold">Gallery Details</Text>
                  <Text>Client: {clientName}</Text>
                  <Text>Title: {galleryTitle}</Text>
                  <Text>Images: {uploadedImages.length}</Text>
                  <Text>Gallery access price: €{galleryPrice}</Text>
                </VStack>

                <Divider />

                <VStack spacing={4}>
                  <Text fontSize="lg" fontWeight="bold">Gallery Token</Text>
                  <HStack>
                    <Code p={2} fontSize="lg">{createdGallery}</Code>
                    <IconButton
                      aria-label="Copy token"
                      icon={<CopyIcon />}
                      size="sm"
                      onClick={() => createdGallery && copyToClipboard(createdGallery)}
                    />
                  </HStack>
                </VStack>

                <Divider />

                <VStack spacing={4}>
                  <Text fontSize="lg" fontWeight="bold">Client Link</Text>
                  <HStack>
                    <Link 
                      href={`/${galleryLanguage}/client/${createdGallery}`} 
                      target="_blank" 
                      color="blue.500"
                      fontSize="lg"
                    >
                      /{galleryLanguage}/client/{createdGallery}
                    </Link>
                    <IconButton
                      aria-label="Open gallery"
                      icon={<ExternalLinkIcon />}
                      size="sm"
                      onClick={() => createdGallery && window.open(`/${galleryLanguage}/client/${createdGallery}`, '_blank')}
                    />
                    <IconButton
                      aria-label="Copy link"
                      icon={<CopyIcon />}
                      size="sm"
                      onClick={() => createdGallery && copyToClipboard(`${window.location.origin}/${galleryLanguage}/client/${createdGallery}`)}
                    />
                  </HStack>
                </VStack>

                <Alert status="success">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Gallery is ready!</Text>
                    <Text>All images have been processed and are ready for viewing.</Text>
                    <Text>Share the client link above with your client.</Text>
                  </Box>
                </Alert>

                <Button onClick={resetForm} colorScheme="blue">
                  Create Another Gallery
                </Button>
              </VStack>
            </CardBody>
          </Card>
        )}
        </VStack>
      </Box>
    </AdminAuth>
  );
};

export default AdminGallery;